import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { trainingService } from '@/services/trainingService'
import { employeesService } from '@/services/employeesService'
import type { TrainingVm } from '@/api/types/training.types'
import { showError, showSuccess } from '@/lib/swal'

export const TrainingEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const tt = (key: string) => t(`empTraining.${key}`)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    courseTitle: '',
    trainingDonor: '',
    trainingYear: '' as string | number,
    description: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await trainingService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          courseTitle: data.courseTitle ?? data.trainingCourseTitle ?? '',
          trainingDonor: data.trainingDonor ?? '',
          trainingYear: data.trainingYear ?? '',
          description: data.description ?? data.trainingDetails ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? tt('loadFailed'))
        navigate('/trainings')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label =
          fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? t('empTraining.employeeFallback', { id: e.id ?? 0 })
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees, t]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(tt('branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(tt('employeeRequired'))
      return
    }
    if (!form.courseTitle.trim()) {
      toast.error(tt('courseTitleRequired'))
      return
    }
    if (!form.trainingDonor.trim()) {
      toast.error(tt('trainingDonorRequired'))
      return
    }
    const year = typeof form.trainingYear === 'number' ? form.trainingYear : parseInt(String(form.trainingYear), 10)
    if (!Number.isFinite(year) || year < 1900 || year > 2100) {
      toast.error(tt('yearInvalid'))
      return
    }
    if (!form.description.trim()) {
      toast.error(tt('descriptionRequired'))
      return
    }

    const payload: TrainingVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Backend field names (Swagger sample)
      trainingCourseTitle: form.courseTitle.trim(),
      trainingDonor: form.trainingDonor.trim(),
      trainingYear: year,
      trainingDetails: form.description.trim(),

      // Frontend field names (compatibility)
      courseTitle: form.courseTitle.trim(),
      description: form.description.trim(),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await trainingService.update(payload)
        await showSuccess(tt('updatedTitle'), tt('updatedMessage'))
      } else {
        await trainingService.save(payload)
        await showSuccess(tt('savedTitle'), tt('savedMessage'))
      }
      navigate('/trainings')
    } catch (e: any) {
      await showError(tt('saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? tt('saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={tt('formTitle')} description={tt('formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{tt('selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={tt('formTitle')}
        description={isEditing ? tt('formDescriptionEdit') : tt('formDescriptionAdd')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{tt('cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>
                {tt('requiredMark')} {tt('labelEmployee')}
              </Label>
              <SearchableSelect
                options={[{ value: '0', label: tt('placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder={tt('placeholderSelectEmployee')}
                searchPlaceholder={tt('searchEmployeePlaceholder')}
                emptyMessage={tt('emptyEmployee')}
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tt('requiredMark')} {tt('labelCourseTitle')}
              </Label>
              <Input
                value={form.courseTitle}
                onChange={(e) => handleChange({ courseTitle: e.target.value })}
                placeholder={tt('placeholderCourseTitle')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tt('requiredMark')} {tt('labelTrainingDonor')}
              </Label>
              <Input
                value={form.trainingDonor}
                onChange={(e) => handleChange({ trainingDonor: e.target.value })}
                placeholder={tt('placeholderTrainingDonor')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {tt('requiredMark')} {tt('labelYear')}
              </Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.trainingYear === '' ? '' : form.trainingYear}
                onChange={(e) => handleChange({ trainingYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder={tt('placeholderYear')}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>
              {tt('requiredMark')} {tt('labelDescription')}
            </Label>
            <Textarea
              value={form.description}
              onChange={(e) => handleChange({ description: e.target.value })}
              placeholder={tt('placeholderDescription')}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/trainings')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {tt('return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
